import { websiteBuilderGetSectionVisibility } from "pages/secure/facility/websiteBuilder/editor/nodes/section/websiteBuilderSection";
import React, { useLayoutEffect, useState } from "react";
import { TWebsiteBuilderNode } from "../nodes/websiteBuilderNode";
import { WebsiteBuilderRootView } from "./views/components/root/WebsiteBuilderRootView";
import "./websiteBuilderDesignSpace.scss";
import "./styles/editor.scss";
import { useAppSelector } from "hooks/redux";
import { WebsiteBuilderSectionView } from "./views/components/section/WebsiteBuilderSectionView";
import { WEBSITE_BUILDER_Z_LAYERS } from "../utility/websiteBuilderZLayers";
import { useWebsiteBuilderEditorContext } from "../websiteBuilderEditorContext";
import { IWebsiteBuilderDesignSpaceBoundaries } from "../WebsiteBuilderEditor";
import { WebsiteBuilderElementViewContextProvider } from "./views/components/element/WebsiteBuilderElementView";
import { WebsiteBuilderContainerViewContextProvider } from "./views/components/container/WebsiteBuilderContainerView";
import { WebsiteBuilderOverlay } from "./overlay/WebsiteBuilderOverlay";
import classNames from "classnames";
import { WEBSITE_BUILDER_DESIGN_SPACE_BOUNDARY_DESKTOP_WIDTH } from "../website/shared/device";
import { websiteBuilderGridCellFilled } from "../nodes/gridCell/websiteBuilderGridCell";
import { WebsiteBuilderGridCellView } from "./views/components/gridCell/WebsiteBuilderGridCellView";
import { isWebsiteBuilderGrid } from "../nodes/container/websiteBuilderGrid";
import { isWebsiteBuilderTabset } from "../nodes/container/websiteBuilderTabset";
import { WebsiteBuilderTabView } from "./views/components/tab/WebsiteBuilderTabView";

interface IWebsiteBuilderDesignSpaceProps {
  setDesignSpaceBoundaries: React.Dispatch<React.SetStateAction<IWebsiteBuilderDesignSpaceBoundaries>>;
}

interface IDesignSpaceSectionResizeState {
  scrollBarExists: boolean;
  bottomSpacerHeight: number;
}

export function WebsiteBuilderDesignSpace(props: IWebsiteBuilderDesignSpaceProps) {
  const { setDesignSpaceBoundaries } = props;

  const { scrollContainerRef, designSpaceRef, designSpaceBoundaries } = useWebsiteBuilderEditorContext();

  const deviceView = useAppSelector(store => store.websiteBuilderStore.editor.deviceView);
  const selectedNode = useAppSelector(store => store.websiteBuilderStore.editor.selectedNode);
  const header = useAppSelector(store => store.websiteBuilderStore.editor.header);
  const footer = useAppSelector(store => store.websiteBuilderStore.editor.footer);
  const pageMap = useAppSelector(store => store.websiteBuilderStore.editor.pageMap);
  const activePageId = useAppSelector(store => store.websiteBuilderStore.editor.activePageId);

  const editingModeType = selectedNode?.editingMode?.type;

  const activePage = pageMap.get(activePageId);

  const [sectionResizeState, setSectionResizeState] = useState<IDesignSpaceSectionResizeState>({
    scrollBarExists: false,
    bottomSpacerHeight: 0,
  });

  useLayoutEffect(() => {
    let observer: ResizeObserver = null;
    let sectionResizing = false;

    const scrollContainer = scrollContainerRef.current;
    const designSpace = designSpaceRef.current;

    if (scrollContainer == null || designSpace == null || deviceView == null) {
      return;
    }

    sectionResizing = editingModeType === "section-resize";

    const update = () => {
      if (sectionResizing && scrollContainer != null) {
        requestAnimationFrame(() => {
          const scrollBarExists = scrollContainer.scrollHeight > scrollContainer.clientHeight;
          const bottomSpacerHeight = scrollBarExists ? scrollContainer.clientHeight : 0;

          setSectionResizeState(prev => ({
            ...prev,
            scrollBarExists,
            bottomSpacerHeight,
          }));
        });
      }

      requestAnimationFrame(() => {
        if (designSpace == null || deviceView == null) {
          return;
        }

        if (deviceView === "desktop") {
          const designSpaceWidth = designSpace.getBoundingClientRect().width;

          const boundaryGap = (designSpaceWidth - WEBSITE_BUILDER_DESIGN_SPACE_BOUNDARY_DESKTOP_WIDTH) / 2;

          const updatedLeftX = boundaryGap;
          const updatedRightX = designSpaceWidth - boundaryGap;

          setDesignSpaceBoundaries(prev => {
            if (Math.abs(prev.leftX - updatedLeftX) < 1 && Math.abs(prev.rightX - updatedRightX) < 1) {
              return prev;
            } else {
              return {
                leftX: updatedLeftX,
                rightX: updatedRightX,
                width: updatedRightX - updatedLeftX,
              };
            }
          });
        } else {
          const designSpaceWidth = designSpace.getBoundingClientRect().width;

          const updatedLeftX = 0;
          const updatedRightX = designSpaceWidth;

          setDesignSpaceBoundaries(prev => {
            if (Math.abs(prev.leftX - updatedLeftX) < 1 && Math.abs(prev.rightX - updatedRightX) < 1) {
              return prev;
            } else {
              return {
                leftX: updatedLeftX,
                rightX: updatedRightX,
                width: updatedRightX - updatedLeftX,
              };
            }
          });
        }
      });
    };

    update();

    observer = new ResizeObserver(update);
    observer.observe(designSpace);

    return () => {
      observer?.disconnect();

      if (sectionResizing) {
        setSectionResizeState(prev => ({
          ...prev,
          scrollBarExists: false,
          bottomSpacerHeight: 0,
        }));
      }
    };
  }, [editingModeType, deviceView]);

  function renderNodeView(
    node: TWebsiteBuilderNode,
    parentNode: TWebsiteBuilderNode,
    grandParentNode: TWebsiteBuilderNode,
    greatGrandParentNode: TWebsiteBuilderNode,
  ): JSX.Element {
    if (node?.type === "root") {
      return (
        <WebsiteBuilderRootView
          key={node.id}
          node={node}
          header={renderNodeView(header, node, null, null)}
          footer={renderNodeView(footer, node, null, null)}
        >
          {node.children?.map(child => renderNodeView(child, node, null, null))}
        </WebsiteBuilderRootView>
      );
    } else if (node?.type === "section" && parentNode?.type === "root" && websiteBuilderGetSectionVisibility(node, parentNode)[deviceView]) {
      return (
        <WebsiteBuilderSectionView key={node.id} root={parentNode} node={node}>
          {node.children?.map(child => renderNodeView(child, node, parentNode, null))}
        </WebsiteBuilderSectionView>
      );
    } else if (node?.type === "container" && parentNode?.type === "section" && grandParentNode?.type === "root" && node.container.visibility[deviceView]) {
      return (
        <WebsiteBuilderContainerViewContextProvider
          key={node.id}
          node={node}
          parentNode={parentNode}
          grandParentNode={grandParentNode}
        >
          {node.children?.map(child => renderNodeView(child, node, parentNode, grandParentNode))}
        </WebsiteBuilderContainerViewContextProvider>
      );
    } else if (node?.type === "grid-cell" && isWebsiteBuilderGrid(parentNode) && grandParentNode?.type === "section" && greatGrandParentNode?.type === "root") {
      return (
        <WebsiteBuilderGridCellView
          key={node.id}
          node={node}
          gridNode={parentNode}
          sectionNode={grandParentNode}
        >
          {websiteBuilderGridCellFilled(node) ? renderNodeView(node.children[0], node, parentNode, grandParentNode) : null}
        </WebsiteBuilderGridCellView>
      );
    } else if (node?.type === "tab" && isWebsiteBuilderTabset(parentNode) && grandParentNode?.type === "section" && greatGrandParentNode?.type === "root") {
      return (
        <WebsiteBuilderTabView
          key={node.id}
          node={node}
          tabsetNode={parentNode}
          sectionNode={grandParentNode}
        >
          {node.children?.map(child => renderNodeView(child, node, parentNode, grandParentNode))}
        </WebsiteBuilderTabView>
      );
    } else if (node?.type === "element" && node.element.visibility[deviceView]) {
      return (
        <WebsiteBuilderElementViewContextProvider
          key={node.id}
          node={node}
          parentNode={parentNode}
          grandParentNode={grandParentNode}
          greatGrandParentNode={greatGrandParentNode}
        />
      );
    } else {
      return null;
    }
  }

  return (
    <div
      className="website-builder-design-space-outer-frame"
      style={{
        zIndex: WEBSITE_BUILDER_Z_LAYERS.designSpace.outerFrame,
      }}
      data-design-space-device={deviceView}
    >
      <div
        ref={scrollContainerRef}
        className={classNames({
          "design-space-scroll-container-desktop": deviceView === "desktop",
          "design-space-scroll-container-tablet": deviceView === "tablet",
          "design-space-scroll-container-mobile": deviceView === "mobile",
        })}
      >
        <div
          ref={designSpaceRef}
          className={classNames({
            "design-space-desktop": deviceView === "desktop",
            "design-space-tablet": deviceView === "tablet",
            "design-space-mobile": deviceView === "mobile",
          })}
        >
          {renderNodeView(activePage?.root, null, null, null)}

          {editingModeType === "section-resize" && sectionResizeState.scrollBarExists && (
            <div style={{ height: sectionResizeState.bottomSpacerHeight }} />
          )}

          {deviceView === "desktop" && (
            <>
              <div
                className="design-space-desktop-boundary"
                style={{
                  left: designSpaceBoundaries.leftX,
                  zIndex: WEBSITE_BUILDER_Z_LAYERS.designSpace.sectionEditorUI,
                }}
              />
              <div
                className="design-space-desktop-boundary"
                style={{
                  left: designSpaceBoundaries.rightX,
                  zIndex: WEBSITE_BUILDER_Z_LAYERS.designSpace.sectionEditorUI,
                }}
              />
            </>
          )}

          <WebsiteBuilderOverlay />
        </div>
      </div>
    </div>
  );
}